<template>
  <div class="step-one">
    <div class="text-overline font-weight-semibold mb-1">Submit a New Listing</div>
    <h2 class="text-h5 font-weight-bold mb-4">Select Cruise Brand, Ship, and Sail Date</h2>

    <v-card elevation="3" rounded="lg" class="pa-6">
      <v-alert v-if="notFound" type="error" variant="tonal" class="mb-4" border="start" title="No sailing found"
        text="No sailing found for the selected ship and date." />

      <v-form ref="formRef" v-model="isValid">
        <v-row dense>
          <!-- Brand -->
          <v-col cols="12" md="4">
            <v-select v-model="selectedBrand" :items="cruiseBrands" label="Select Cruise Brand" variant="outlined"
              density="comfortable" :rules="[v => !!v || 'Please select a brand']" prepend-inner-icon="mdi-ferry"
              @update:model-value="onBrandChange" clearable />
          </v-col>

          <!-- Ship -->
          <v-col cols="12" md="4">
            <v-select v-model="selectedShip" :items="filteredShips" item-title="name" item-value="name"
              label="Select Ship" variant="outlined" density="comfortable" :disabled="!selectedBrand || shipsLoading"
              :loading="shipsLoading" :rules="[v => !!v || 'Please select a ship']" prepend-inner-icon="mdi-ferry"
              clearable @update:model-value="onShipChange" />
          </v-col>

          <!-- Sail Date (Dialog Date Picker) -->
          <v-col cols="12" md="4">
            <v-dialog v-model="dateDialog" max-width="360">
              <template #activator="{ props }">
                <v-text-field v-bind="props" :model-value="displaySailDate || ''" label="Select Sail Date" variant="outlined"
                  density="comfortable" prepend-inner-icon="mdi-calendar" readonly :loading="datesLoading"
                  :disabled="!selectedShip || datesLoading || validDates.length === 0" :rules="[
                    () => !!sailDate || 'Please choose a date',
                    () => !sailDate || isAllowedDate(sailDate) || 'Pick a valid sail date'
                  ]" @click="openDateDialog" clearable @click:clear="clearDate"
                  hint="Only upcoming dates with available sailings are enabled" persistent-hint />
              </template>

              <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  Choose a date
                </v-card-title>
                <v-divider />
                <v-card-text>
                  <v-date-picker v-model="tempDate" :allowed-dates="isAllowedDate" hide-header
                    @update:model-value="onDatePicked" />
                </v-card-text>
                <v-divider />
                <v-card-actions class="justify-end">
                  <v-btn variant="text" @click="dateDialog = false">Cancel</v-btn>
                  <v-btn color="primary" @click="saveDate" :disabled="isSaveDisabled">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <div v-if="selectedShip && !datesLoading" class="text-caption mt-1">
              {{ validDates.length }} upcoming valid date{{ validDates.length === 1 ? '' : 's' }} available
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4" class="mx-auto">
            <v-btn color="primary" size="large" block @click="onSearch" :loading="searching">
              <v-icon class="mr-2">mdi-magnify</v-icon>
              Search Sailing
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import { supabase } from "@/supabaseClient";

export default {
  name: "StepOneSearch",
  data() {
    return {
      cruiseBrands: [],
      selectedBrand: "",
      filteredShips: [],
      selectedShip: "",
      sailDate: "", // YYYY-MM-DD

      notFound: false,
      foundItinerary: null,
      isValid: false,
      shipsLoading: false,
      searching: false,

      // date picker dialog state
      dateDialog: false,
      tempDate: null, // string YYYY-MM-DD

      // valid dates for the selected ship (upcoming only)
      validDates: [],           // array of 'YYYY-MM-DD'
      validDatesSet: new Set(), // for O(1) lookup
      datesLoading: false,
    };
  },
  computed: {
    isSaveDisabled() {
      return !this.tempDate || !this.isAllowedDate(this.tempDate);
    },
    displaySailDate() {
      return this.formatIsoToCruise(this.sailDate);
    },
  },
  methods: {
    async fetchCruiseBrands() {
      try {
        const { data, error } = await supabase.from("ships").select("cruise_line");
        if (error) throw error;
        const brandSet = new Set();
        (data || []).forEach(r => r.cruise_line && brandSet.add(r.cruise_line));
        this.cruiseBrands = Array.from(brandSet).sort();
      } catch (err) {
        console.error("Error fetching brands:", err);
      }
    },

    onBrandChange() {
      // reset downstream fields when brand changes
      this.selectedShip = "";
      this.filteredShips = [];
      this.sailDate = "";
      this.validDates = [];
      this.validDatesSet = new Set();
      this.loadShips();
    },

    async loadShips() {
      this.shipsLoading = true;
      try {
        const { data, error } = await supabase
          .from("ships")
          .select("ship_name, ship_image, cruise_line, ship_id")
          .eq("cruise_line", this.selectedBrand)
          .order("ship_name", { ascending: true });
        if (error) throw error;

        this.filteredShips = (data || []).map(s => ({
          name: s.ship_name,
          picture: s.ship_image,
          cruiseLine: s.cruise_line,
          id: s.ship_id,
        }));
      } catch (err) {
        console.error("Error fetching ships:", err);
      } finally {
        this.shipsLoading = false;
      }
    },

    async onShipChange() {
      // clear date and valid dates when ship changes
      this.sailDate = "";
      this.tempDate = null;
      this.validDates = [];
      this.validDatesSet = new Set();

      const ship = this.filteredShips.find(s => s.name === this.selectedShip);
      if (!ship) return;

      await this.loadValidDates(ship.id);
    },

    async loadValidDates(shipID) {
      this.datesLoading = true;
      try {
        const { data, error } = await supabase
          .from("cruises")
          .select("date")
          .eq("ship_id", shipID)
          .order("date", { ascending: true });

        if (error) throw error;

        // Normalize to YYYY-MM-DD and unique
        const all = (data || [])
          .map(r => (r.date ? String(r.date).slice(0, 10) : null))
          .filter(Boolean);

        // Keep only dates >= today (local)
        const today = this.todayIso();
        const upcoming = all.filter(d => d >= today);

        this.validDates = Array.from(new Set(upcoming)).sort();
        this.validDatesSet = new Set(this.validDates);

        // pick the next upcoming date, if any
        this.tempDate = this.validDates[0] || null;
      } catch (err) {
        console.error("Error fetching valid dates:", err);
        this.validDates = [];
        this.validDatesSet = new Set();
      } finally {
        this.datesLoading = false;
      }
    },

    // Local YYYY-MM-DD (avoids UTC off-by-one)
    todayIso() {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    // Normalize to 'YYYY-MM-DD'
    normalizeToIso(val) {
      if (!val) return "";
      if (typeof val === "string") return val.slice(0, 10);
      const d = new Date(val);
      if (isNaN(d)) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    // Allowed only if date exists for ship AND is today or later
    isAllowedDate(val) {
      const iso = this.normalizeToIso(val);
      if (!iso) return false;
      if (iso < this.todayIso()) return false;
      return this.validDatesSet.has(iso);
    },

    onDatePicked(val) {
      this.tempDate = this.normalizeToIso(val);
    },

    openDateDialog() {
      // ensure tempDate is valid when opening
      if (!this.isAllowedDate(this.sailDate)) {
        this.tempDate = this.validDates[0] || null;
      } else {
        this.tempDate = this.sailDate;
      }
      this.dateDialog = true;
    },

    saveDate() {
      if (!this.isAllowedDate(this.tempDate)) return;
      this.sailDate = this.normalizeToIso(this.tempDate);
      this.dateDialog = false;
    },

    clearDate() {
      this.sailDate = "";
      this.tempDate = null;
    },

    async onSearch() {
      this.notFound = false;
      const formOk = await this.$refs.formRef.validate();
      if (!formOk.valid) return;
      await this.searchItinerary();
    },

    async searchItinerary() {
      if (!this.selectedShip || !this.sailDate) {
        this.notFound = true;
        return;
      }
      const ship = this.filteredShips.find(s => s.name === this.selectedShip);
      const shipID = ship ? ship.id : null;
      if (!shipID) {
        this.notFound = true;
        return;
      }

      this.searching = true;
      try {
        const { data, error } = await supabase
          .from("cruises")
          .select("*")
          .eq("ship_id", shipID)
          .eq("date", this.sailDate)
          .limit(1);

        if (error) throw error;

        if (data && data.length > 0) {
          this.foundItinerary = {
            ...data[0],
            ship,
            sailingID: data[0].id,
          };
          this.notFound = false;
          this.$emit("next", this.foundItinerary);
        } else {
          this.foundItinerary = null;
          this.notFound = true;
        }
      } catch (err) {
        console.error("Supabase error:", err);
        this.notFound = true;
      } finally {
        this.searching = false;
      }
    },
    // 'YYYY-MM-DD' -> '01JAN2026'
    formatIsoToCruise(iso) {
      if (!iso) return ''
      // defend against other inputs
      const s = String(iso).slice(0, 10)
      const [y, m, d] = s.split('-')
      if (!y || !m || !d) return ''
      const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
      const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1))
      const dd = d.padStart(2, '0')
      const mon = MONTHS[idx] || ''
      return `${dd}${mon}${y}`
    },
  },
  created() {
    this.fetchCruiseBrands();
  },
};
</script>

<style scoped>
.step-one {
  max-width: 1500px;
  margin: 0 auto;
}
</style>
